/**
 * shapeNormalization.ts
 * @deprecated Ce module n'est actuellement pas utilisé dans l'application (imported_by_total = 0).
 * Il est conservé pour référence future mais peut être supprimé si non nécessaire.
 *
 * P1.4 FIX: Normalisation des ShapeData
 * Garantit que tous les champs nécessaires sont initialisés avec des valeurs par défaut
 * Évite les bugs dus à des champs optionnels incohérents
 */
import type { ShapeData, ShapeType } from '../types/ShapeTypes';

/**
 * Valeurs par défaut pour les champs communs
 */
const DEFAULT_SHAPE_VALUES = {
  opacity: 1,
  visible: true,
  locked: false,
  rotation: 0,
  zIndex: 0,
} as const;

/**
 * Valeurs par défaut spécifiques par type
 */
const TYPE_DEFAULTS: Record<ShapeType, Partial<ShapeData>> = {
  rect: {
    width: 150,
    height: 100,
    fill: '#3b82f6',
    cornerRadius: 0,
  },
  circle: {
    width: 100,
    height: 100,
    fill: '#10b981',
  },
  text: {
    text: 'Nouveau texte',
    fontSize: 24,
    fontFamily: 'Arial',
    fill: '#1f2937',
    textAlign: 'left',
    fontWeight: 'normal',
  },
  image: {
    width: 200,
    height: 150,
    src: '',
  },
  triangle: {
    width: 100,
    height: 100,
    fill: '#f59e0b',
  },
  star: {
    width: 100,
    height: 100,
    fill: '#eab308',
  },
  heart: {
    width: 100,
    height: 100,
    fill: '#ef4444',
  },
  arrow: {
    width: 100,
    height: 20,
    fill: '#6366f1',
  },
  line: {
    width: 100,
    height: 2,
    stroke: '#000000',
    strokeWidth: 2,
  },
  group: {
    children: [],
  },
  photoSpace: {
    width: 200,
    height: 150,
    fill: '#e5e7eb',
    photoIndex: 1,
    fitMode: 'cover',
  },
};

/**
 * Normalise une ShapeData en garantissant que tous les champs nécessaires sont initialisés
 *
 * @param shape - ShapeData à normaliser
 * @returns ShapeData normalisé avec tous les champs nécessaires
 */
export function normalizeShapeData(shape: ShapeData): ShapeData {
  // Appliquer les valeurs par défaut communes
  const normalized: ShapeData = {
    ...shape,
    opacity: shape.opacity ?? DEFAULT_SHAPE_VALUES.opacity,
    visible: shape.visible ?? DEFAULT_SHAPE_VALUES.visible,
    locked: shape.locked ?? DEFAULT_SHAPE_VALUES.locked,
    rotation: shape.rotation ?? DEFAULT_SHAPE_VALUES.rotation,
    zIndex: shape.zIndex ?? DEFAULT_SHAPE_VALUES.zIndex,
  };

  // Appliquer les valeurs par défaut spécifiques au type
  const typeDefaults = TYPE_DEFAULTS[shape.type];
  if (typeDefaults) {
    for (const [key, value] of Object.entries(typeDefaults)) {
      if (normalized[key as keyof ShapeData] === undefined) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (normalized as any)[key] = value;
      }
    }
  }

  // Validation spécifique par type
  switch (shape.type) {
    case 'rect':
    case 'circle':
    case 'triangle':
    case 'star':
    case 'heart':
    case 'arrow':
    case 'image':
    case 'photoSpace':
      // Ces types nécessitent width et height
      if (!normalized.width || normalized.width <= 0) {
        normalized.width = typeDefaults?.width ?? 100;
      }
      if (!normalized.height || normalized.height <= 0) {
        normalized.height = typeDefaults?.height ?? 100;
      }
      break;

    case 'text':
      // Le texte nécessite au minimum un texte
      if (!normalized.text) {
        normalized.text = 'Nouveau texte';
      }
      if (!normalized.fontSize || normalized.fontSize <= 0) {
        normalized.fontSize = 24;
      }
      if (!normalized.fontFamily) {
        normalized.fontFamily = 'Arial';
      }
      break;

    case 'group':
      // Les groupes nécessitent un tableau children
      if (!normalized.children) {
        normalized.children = [];
      }
      // Normaliser récursivement les enfants
      normalized.children = normalized.children.map(child => normalizeShapeData(child));
      break;
  }

  return normalized;
}

/**
 * Valide qu'une ShapeData est complète et valide
 *
 * @param shape - ShapeData à valider
 * @returns true si la shape est valide, false sinon
 */
export function isValidShapeData(shape: ShapeData): boolean {
  // Vérifications de base
  if (!shape.id || !shape.type || typeof shape.x !== 'number' || typeof shape.y !== 'number') {
    return false;
  }

  // Vérifications spécifiques par type
  switch (shape.type) {
    case 'rect':
    case 'circle':
    case 'triangle':
    case 'star':
    case 'heart':
    case 'arrow':
    case 'image':
    case 'photoSpace':
      if (!shape.width || shape.width <= 0 || !shape.height || shape.height <= 0) {
        return false;
      }
      break;

    case 'text':
      if (!shape.text) {
        return false;
      }
      break;

    case 'group':
      if (!Array.isArray(shape.children)) {
        return false;
      }
      // Valider récursivement les enfants
      return shape.children.every(child => isValidShapeData(child));
  }

  return true;
}
